import { useEffect, useState } from 'react';
import { admin_api } from '@/lib/api';
import {
  Envelope, Calendar, CurrencyDollar, UserPlus, ArrowUp, Van, IdentificationBadge,
  Warning, DownloadSimple, ClipboardText
} from '@phosphor-icons/react';

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
          <Icon size={18} weight="duotone" className="text-brand" />
        </div>
        {sub != null && (
          <div className="text-xs font-semibold text-brand flex items-center gap-1">
            <ArrowUp size={12} weight="bold" /> {sub} new
          </div>
        )}
      </div>
      <div className="font-display font-extrabold text-3xl text-ink">{value ?? '—'}</div>
      <div className="text-sm text-ink-muted mt-1">{label}</div>
    </div>
  );
}

const EXPORTS = ['bookings', 'enquiries', 'quotes', 'referrals', 'fleet', 'drivers', 'incidents', 'audit'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    admin_api.stats().then(setStats).finally(() => setLoading(false));
    admin_api.alerts().then(r => setAlerts(r.items || [])).catch(() => {});
  }, []);

  const download = async (kind) => {
    const token = localStorage.getItem('swift_admin_token');
    const res = await fetch(admin_api.exportUrl(kind), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `swift_${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="overline mb-3">Operations</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl text-ink">Dashboard</h1>
        <p className="text-ink-muted mt-2">Overview of everything flowing through Inclusive Mobility Group right now.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <StatCard testId="stat-today-bookings" icon={Calendar} label="Bookings today" value={stats?.today_bookings} />
        <StatCard testId="stat-unassigned" icon={ClipboardText} label="Awaiting assignment" value={stats?.unassigned_bookings} />
        <StatCard testId="stat-vehicles-available" icon={Van} label={`Vehicles available / ${stats?.vehicles_total ?? '—'}`} value={stats?.vehicles_available} />
        <StatCard testId="stat-drivers-on-shift" icon={IdentificationBadge} label={`Drivers on shift / ${stats?.drivers_total ?? '—'}`} value={stats?.drivers_on_shift} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
        <StatCard testId="stat-revenue-today" icon={CurrencyDollar} label="Revenue today" value={stats ? `$${stats.revenue_today}` : null} />
        <StatCard testId="stat-revenue-week" icon={CurrencyDollar} label="Revenue (7 days)" value={stats ? `$${stats.revenue_week}` : null} />
        <StatCard testId="stat-revenue-month" icon={CurrencyDollar} label="Revenue (30 days)" value={stats ? `$${stats.revenue_month}` : null} />
        <StatCard testId="stat-conversion" icon={ArrowUp} label="Quote → booking rate" value={stats ? `${stats.conversion_rate}%` : null} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
        <StatCard testId="stat-enquiries" icon={Envelope} label="Enquiries" value={stats?.total_enquiries} sub={stats?.new_enquiries} />
        <StatCard testId="stat-bookings" icon={Calendar} label="Bookings" value={stats?.total_bookings} sub={stats?.pending_bookings} />
        <StatCard testId="stat-quotes" icon={CurrencyDollar} label="Quotes" value={stats?.total_quotes} sub={stats?.new_quotes} />
        <StatCard testId="stat-referrals" icon={UserPlus} label="Referrals" value={stats?.total_referrals} sub={stats?.new_referrals} />
      </div>

      {alerts.length > 0 && (
        <div className="mt-10 p-6 rounded-2xl bg-white border border-amber-200" data-testid="admin-alerts-panel">
          <div className="flex items-center gap-2 mb-4">
            <Warning size={20} weight="fill" className="text-amber-500" />
            <h2 className="font-display font-bold text-xl text-ink">Compliance alerts ({alerts.length})</h2>
          </div>
          <div className="divide-y divide-neutral-100">
            {alerts.slice(0, 8).map((a, i) => (
              <div key={i} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <span className="font-semibold text-ink">{a.name}</span>
                  <span className="text-ink-muted"> — {a.item}</span>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${a.severity === 'overdue' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                  {a.severity === 'overdue' ? `OVERDUE (${a.date})` : `${a.days_left}d — ${a.date}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 p-6 rounded-2xl bg-white border border-neutral-200" data-testid="admin-exports-panel">
        <h2 className="font-display font-bold text-xl text-ink mb-4">Reports & exports (CSV)</h2>
        <div className="flex flex-wrap gap-2">
          {EXPORTS.map(k => (
            <button key={k} onClick={() => download(k)} data-testid={`export-${k}`} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-ink-soft hover:border-brand hover:text-brand transition">
              <DownloadSimple size={14} /> {k}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 p-6 rounded-2xl bg-white border border-neutral-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-xl text-ink">Recent activity</h2>
          <div className="text-xs text-ink-muted">Last {stats?.recent_activity?.length || 0} items</div>
        </div>
        {loading ? (
          <div className="text-ink-muted text-sm">Loading…</div>
        ) : (
          <div className="divide-y divide-neutral-100" data-testid="admin-recent-activity">
            {(stats?.recent_activity || []).map((a, i) => (
              <div key={i} className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <span className="badge-brand !text-xs !uppercase">{a.type}</span>
                  <div>
                    <div className="font-medium text-ink text-sm">{a.name}</div>
                    <div className="text-xs text-ink-muted">{a.created_at ? new Date(a.created_at).toLocaleString('en-AU') : ''}</div>
                  </div>
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest text-ink-soft">{a.status}</div>
              </div>
            ))}
            {(!stats?.recent_activity?.length) && (
              <div className="text-ink-muted text-sm py-6">No activity yet — new submissions will appear here in real time.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
